import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, ModalFooter } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import FormInput from '../../../../../../../../../../components/form/FormInput';
import ButtonGroup from '../../../../../../../../../../components/Modal/ModalButtonGroups/ButtonGroup';
import LoadingSpinner from '../../../../../../../../../../components/Spinner';
import { ChakraSizes } from '../../../../../../../../../../constants/chakraSizes';
import CompanySettingTypes from '../../../../../../../../../../constants/companySettings';
import { CypressIds } from '../../../../../../../../../../constants/cypressIds';
import { InsuredProfilePageDuskIds } from '../../../../../../../../../../constants/duskIds';
import { getValidationObject } from '../../../../../../../../../../constants/regexPatterns';
import { selectCompanySettingValue } from '../../../../../../../../../../redux/modules/company';
import {
  emailEditorReset,
  getAllCompanyEmailTemplates,
  previewEmail,
  selectCurrentMessageHtml,
  selectCurrentSignature,
  selectCurrentSubjectHtml,
  selectDefaultTemplate,
  sendEmail,
  setDefaultTemplates,
  setInsuranceRequestInputs
} from '../../../../../../../../../../redux/modules/emails';
import { defaultEmailTemplateTypes } from '../../../../../../../../../../redux/modules/emails/defaults';
import { hideModal, showModal, updateModalOptions } from '../../../../../../../../../../redux/modules/modal';
import { useInsuredData } from '../../../../../../../../../../services/insured-service';
import insuredKeys from '../../../../../../../../../../services/models/insured/keys';
import { useRecordData } from '../../../../../../../../../../services/record-service';
import { queryClient } from '../../../../../../../../../../utils/queryClientUtil';
import EmailBody from '../../../../../../../../../EmailEditorPage/components/EmailComponents/EmailBody';
import EmailSignature from '../../../../../../../../../EmailEditorPage/components/EmailComponents/EmailSignature';
import PreviewModal from '../../../../../../../../../EmailEditorPage/components/PreviewModal';

const RequestForInsuranceModal = ({ insuredId, recordId, jobId }) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();

  const { data: insured } = useInsuredData(insuredId);
  const { data: record, isLoading: recordIsLoading } = useRecordData(recordId);

  const [isSubmitting, setSubmitting] = useState(false);

  const defaultTemplate = useSelector(selectDefaultTemplate);
  const emailSubject = useSelector(selectCurrentSubjectHtml);
  const emailBody = useSelector(selectCurrentMessageHtml);
  const emailSignature = useSelector(selectCurrentSignature);
  const companyRecordEmailSettingOn = useSelector(selectCompanySettingValue(CompanySettingTypes.IncludeRecordAndInsuredContactInfoInEmailTools));

  useEffect(() => {
    dispatch(getAllCompanyEmailTemplates(companyId));

    if (!defaultTemplate) {
      dispatch(setDefaultTemplates(companyId, defaultEmailTemplateTypes.insuranceRequestDefaultTemplate));
    }
  }, [companyId, defaultTemplate]);

  const useFormMethods = useForm();

  useEffect(() => {
    if (record) {
      const emailRecipients = [];
      const emailCcs = [];
      const recordContactExists = record?.contactEmail || record?.contactCc;

      if (record?.contactEmail) {
        emailRecipients.push(record.contactEmail.split(',').map(email => email.trim()).join(', '));
      }

      if (record?.contactCc) {
        emailCcs.push(record.contactCc.split(',').map(email => email.trim()).join(', '));
      }

      if (insured?.insuredEmail) {
        if (companyRecordEmailSettingOn) {
          emailRecipients.push(insured?.insuredEmail);
        } else if (!recordContactExists) {
          emailRecipients.push(insured?.insuredEmail);
        }
      }

      if (insured?.insuredCc) {
        const cleanCc = insured?.insuredCc.split(',').map(email => email.trim()).join(', ');
        if (companyRecordEmailSettingOn) {
          emailCcs.push(cleanCc);
        } else if (!recordContactExists) {
          emailCcs.push(cleanCc);
        }
      }

      useFormMethods.setValue('email_recipients', emailRecipients.join(', '));
      useFormMethods.setValue('email_ccs', emailCcs.join(', '));
    }
  }, [record, companyRecordEmailSettingOn]);

  const { handleSubmit } = useFormMethods;

  const handleFormSubmit = async data => {
    setSubmitting(true);
    dispatch(updateModalOptions(
      { buttons: { primaryButtonOptions: { isLoading: true } } }
    ));

    const emailData = Object.assign(data, {
      record_id: recordId,
      insured_id: insuredId,
      job_id: jobId,
      email_body: `${emailBody}<br />${emailSignature}`,
      email_subject: emailSubject
    });

    const onSuccess = async () => {
      queryClient.invalidateQueries(insuredKeys.insuredEmails(insuredId, recordId));

      await dispatch(hideModal());
      setSubmitting(false);
      dispatch(updateModalOptions(
        { buttons: { primaryButtonOptions: { isLoading: false } } }
      ));

      dispatch(emailEditorReset());
      dispatch(setInsuranceRequestInputs({ to: null, cc: null }));
    };

    const onFail = () => setSubmitting(false);

    dispatch(sendEmail(emailData, onSuccess, onFail));
  };

  const handlePreviewEmail = async data => {
    dispatch(setInsuranceRequestInputs({ to: data.email_recipients, cc: data.email_ccs }));
    dispatch(previewEmail(recordId, insuredId, jobId, null));

    dispatch(showModal(
      () => <PreviewModal recipientTitle="Recipients" />,
      {
        title: 'Preview',
        actionRequired: false,
        size: ChakraSizes.ExtraLarge,
        maxW: 1440,
        withHistory: true,
        center: false,
        buttons: {
          component: ButtonGroup,
          componentOptions: { justify: 'flex-end' },
          primaryButtonOptions: {
            onClick: handleSubmit(handleFormSubmit),
            label: 'Send'
          },
          secondaryButtonOptions: {
            secondaryClick: () => {
              dispatch(hideModal());
              dispatch(emailEditorReset());
              dispatch(setInsuranceRequestInputs({ to: null, cc: null }));
            }
          },
          withHistoryButton: true
        },
        onClose: () => {
          dispatch(hideModal());
          dispatch(emailEditorReset());
          dispatch(setInsuranceRequestInputs({ to: null, cc: null }));
        }
      }
    ));
  };

  if ((!defaultTemplate || recordIsLoading)) return <LoadingSpinner />;

  return (
    <Box>
      <form>
        <FormInput
          required
          inputName="email_recipients"
          labelName="To:"
          useFormMethods={useFormMethods}
          validationRules={{
            ...getValidationObject('commaSeparatedEmailAddress'),
            required: 'Please provide an email address.'
          }}
        />
        <FormInput
          inputName="email_ccs"
          labelName="CC:"
          useFormMethods={useFormMethods}
          validationRules={getValidationObject('commaSeparatedEmailAddress')}
        />

        <Box
          backgroundColor="greyScale.200"
          borderBottomRadius="5px"
          borderTopRadius="5px"
          boxShadow="md"
        >
          <EmailBody
            bodyContainerId="email-body-editor-insured"
            borderTopRadius="5px"
            selectCodeType="popover"
            subjectContainerId="subject-editor-insured"
          />

          <Box py={2}>
            <hr />
          </Box>

          <EmailSignature
            backgroundColor="greyScale.200"
            borderBottomRadius="5px"
            signatureContainerId="signature-editor"
          />
        </Box>

        <ModalFooter>
          <ButtonGroup
            dataAnalyticsId="send-insurance-request-email-modal-button"
            isLoading={isSubmitting}
            label="Send Email"
            loadingText="Sending"
            secondaryClick={handleSubmit(handlePreviewEmail)}
            secondaryCypressId={CypressIds.previewInsuranceRequestEmailButton}
            secondaryDataAnalyticsId="send-insurance-request-email-modal-preview-button"
            secondaryDuskId={InsuredProfilePageDuskIds.previewInsuranceRequestEmailButton}
            secondaryLabel="Preview Email"
            size="lg"
            width="140px"
            onClick={handleSubmit(handleFormSubmit)}
          />
        </ModalFooter>
      </form>
    </Box>
  );
};

RequestForInsuranceModal.propTypes = {
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired
};

export default RequestForInsuranceModal;
